import "./index.scss";
import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
// IMPORT THÊM ChevronLeft, ChevronRight
import { Film, Music4, Headphones, BarChart3, UsersRound, DollarSign, ChevronLeft, ChevronRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useDonates } from "../../context/DonateContext";

const COLORS = ["#7c3aed", "#2563eb", "#16a34a", "#ca8a04", "#db2672", "#0891b2", "#ea580c", "#ec4899", "#f97316"];

const CATEGORY_NAMES = {
  nhactre: "Nhạc Trẻ",
  nhacphonk: "Nhạc Phonk",
  nhacusuk: "Nhạc Âu Mỹ",
  nhactrungquoc: "Nhạc Hoa",
  nhacedm: "Nhạc EDM",
  nhacdouyin: "Nhạc Douyin",
  nhackhongloi: "Không Lời",
  nhactreremix: "Remix",
  "nhac-lofi": "Lofi",
  nhaclofi: "Lofi"
};

const OverView = () => {
  const { donates } = useDonates();

  const [totalSongs, setTotalSongs] = useState(0);
  const [totalListens, setTotalListens] = useState(0);
  const [categoryData, setCategoryData] = useState([]);
  const [user, setUser] = useState(null); 

  // STATE: Quản lý tháng đang xem (mặc định là tháng hiện tại)
  const [viewDate, setViewDate] = useState(new Date());

  const [monthlyDonateData, setMonthlyDonateData] = useState([]);
  const [currentMonthTotal, setCurrentMonthTotal] = useState(0);
  const donateGoal = 500000; 

  const outletCtx = useOutletContext();
  const totalFromParent = outletCtx?.totalUsersCount;

  useEffect(() => {
    if (totalFromParent !== null && totalFromParent !== undefined) {
      setUser(totalFromParent);
    }
  }, [totalFromParent]);

  /* ========================================================= */
  /* LOGIC XỬ LÝ DỮ LIỆU DONATE THEO THÁNG ĐƯỢC CHỌN (viewDate) */
  /* ========================================================= */
  useEffect(() => {
    // Luôn render biểu đồ (kể cả 0đ) thay vì return sớm nếu mảng donates rỗng
    const safeDonates = donates || [];

    const currentYear = viewDate.getFullYear();
    const currentMonth = viewDate.getMonth(); // 0 - 11

    // 1. Lọc donate theo tháng/năm của viewDate
    const currentMonthDonates = safeDonates.filter((item) => {
      if (!item.created_at) return false;
      const donateDate = new Date(item.created_at.replace(" ", "T"));
      return donateDate.getFullYear() === currentYear && donateDate.getMonth() === currentMonth;
    });

    // 2. Tính tổng tiền
    const totalMonthAmount = currentMonthDonates.reduce((sum, item) => sum + (item.amount || 0), 0);
    setCurrentMonthTotal(totalMonthAmount);

    // 3. Gom nhóm theo ngày
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const dailyMap = {};
    
    for (let d = 1; d <= daysInMonth; d++) {
      dailyMap[d] = 0;
    }

    currentMonthDonates.forEach((item) => {
      const donateDate = new Date(item.created_at.replace(" ", "T"));
      const day = donateDate.getDate();
      dailyMap[day] += item.amount || 0;
    });

    const chartDataFormatted = Object.keys(dailyMap).map((day) => ({
      date: `Ngày ${day}`,
      amount: dailyMap[day],
    }));

    setMonthlyDonateData(chartDataFormatted);
  }, [donates, viewDate]); // Thêm viewDate vào dependencies

  /* ===== HÀM CHUYỂN THÁNG ===== */
  const handlePrevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Kiểm tra xem có đang ở tháng hiện tại của thực tế không (để disable nút Next đi tới tương lai)
  const today = new Date();
  const isCurrentMonth = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();

  /* ===== FETCH SONGS & STATS TỪ SYSTEM_STATS (SIÊU NHẸ) ===== */
  useEffect(() => {
    // Gọi đồng thời 3 endpoint thống kê siêu nhẹ từ bảng system_stats
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/api/songs/stats/total-songs`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
      fetch(`${process.env.REACT_APP_API_URL}/api/songs/stats/total-listens`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
      fetch(`${process.env.REACT_APP_API_URL}/api/songs/stats/category-listens`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ]).then(([songsStat, listensStat, categoryListensStat]) => {
      if (songsStat?.totalSongs !== undefined) {
        setTotalSongs(songsStat.totalSongs);
      }
      if (listensStat?.totalListens !== undefined) {
        setTotalListens(listensStat.totalListens);
      }
      if (categoryListensStat && typeof categoryListensStat === "object") {
        const formattedPieData = Object.entries(categoryListensStat)
          .map(([catKey, val]) => ({
            key: catKey,
            name: CATEGORY_NAMES[catKey] || catKey,
            value: typeof val === "number" ? val : Number(val) || 0,
          }))
          .filter((item) => item.value > 0)
          .sort((a, b) => b.value - a.value);

        setCategoryData(formattedPieData);
      }
    });
  }, []);

  const avgListen = totalSongs > 0 ? Math.round(totalListens / totalSongs) : 0;
  const totalCategoryListens = categoryData.reduce((sum, item) => sum + item.value, 0);
  const donatePercentage = Math.min(((currentMonthTotal / donateGoal) * 100), 100).toFixed(1);

  const stats = [
    { title: "Số bài hát", value: totalSongs, icon: <Film />, color: "purple" },
    { title: "Playlist", value: 9, icon: <Music4 />, color: "blue" },
    { title: "Tổng lượt nghe", value: totalListens, icon: <Headphones />, color: "green" },
    { title: "Trung bình lượt / bài", value: avgListen, icon: <BarChart3 />, color: "yellow" },
    { title: "Người dùng đã đăng ký", value: user === null ? "..." : user, icon: <UsersRound />, color: "pink" },
  ];

  return (
    <div className="overview-page">
      <div className="stats-grid">
        {stats.map((item, index) => (
          <div className={`stat-card ${item.color}`} key={index}>
            <div className="card-top">
              <div className="stat-icon-wrapper">{item.icon}</div>
              <p className="stat-title">{item.title}</p>
            </div>
            <div className="card-middle">
              <p className="stat-value">
                {typeof item.value === "number" ? item.value.toLocaleString("vi-VN") : item.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-wrapper">
        <div className="chart-card donate-chart-card">
          <div className="chart-header-donate">
            <div className="header-left">
              <h3>Thống kê quỹ quyên góp</h3>
              
              {/* KHU VỰC CHUYỂN THÁNG MỚI */}
              <div className="month-navigation">
                <button className="nav-btn" onClick={handlePrevMonth}>
                  <ChevronLeft size={16} />
                </button>
                <p className="sub-title">Tháng {viewDate.getMonth() + 1} / {viewDate.getFullYear()}</p>
                <button className="nav-btn" onClick={handleNextMonth} disabled={isCurrentMonth}>
                  <ChevronRight size={16} />
                </button>
              </div>

            </div>
            <div className="header-right-total">
              <DollarSign size={18} className="money-icon" />
              <span>{currentMonthTotal.toLocaleString("vi-VN")}đ</span>
            </div>
          </div>

          <div className="donate-progress-container">
            <div className="progress-text-info">
              <span>Tiến độ duy trì server: <strong>{donatePercentage}%</strong></span>
              <span>{currentMonthTotal.toLocaleString("vi-VN")}đ / {donateGoal.toLocaleString("vi-VN")}đ</span>
            </div>
            <div className="progress-bar-bg">
              <div className="progress-bar-fill" style={{ width: `${donatePercentage}%` }}></div>
            </div>
          </div>

          <div className="chart-content">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyDonateData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a354f" vertical={false} />
                <XAxis dataKey="date" stroke="#94a3b8" tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke="#94a3b8" tickLine={false} axisLine={false} fontSize={11} tickFormatter={(value) => value >= 1000 ? `${value / 1000}K` : value} />
                <Tooltip formatter={(value) => [`${value.toLocaleString("vi-VN")}đ`, "Ủng hộ"]} contentStyle={{ background: "#111827", border: "none", borderRadius: "8px", color: "#fff" }} />
                <Area type="monotone" dataKey="amount" stroke="#10b981" fill="url(#colorDonate)" strokeWidth={2} dot={{ r: 3, stroke: "#10b981", fill: "#1f2937", strokeWidth: 2 }} />
                <defs>
                  <linearGradient id="colorDonate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* BIỂU ĐỒ TRÒN CATEGORY LƯỢT NGHE */}
        <div className="chart-card category-chart">
          <div className="chart-header">
            <h3>Lượt nghe theo danh mục</h3>
          </div>
          <div className="chart-content pie-container">
            <div className="pie-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="50%" innerRadius={65} outerRadius={85} fill="#8884d8" paddingAngle={3} dataKey="value" startAngle={90} endAngle={-270}>
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={4} />
                    ))}
                  </Pie>
                  <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="pie-label-center">
                    <tspan x="50%" dy="-6" className="pie-value">
                      {totalCategoryListens >= 1000 ? `${(totalCategoryListens / 1000).toFixed(1)}K` : totalCategoryListens}
                    </tspan>
                    <tspan x="50%" dy="20" className="pie-title">Lượt nghe</tspan>
                  </text>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="pie-legend">
              {categoryData.map((entry, index) => {
                const percentage = totalCategoryListens > 0 ? ((entry.value / totalCategoryListens) * 100).toFixed(1) : 0;
                return (
                  <div className="legend-item" key={index}>
                    <div className="legend-header">
                      <div className="legend-marker" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                      <span className="legend-name">{entry.name}</span>
                    </div>
                    <span className="legend-value">{entry.value.toLocaleString("vi-VN")} ({percentage}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverView;